<template>
  <div class="field-text-edit position-relative">
    <!-- Botón de lock/unlock para campos safe (solo si no está deshabilitado) -->
    <div v-if="field.safe && !disableLock" class="float-left">
      <button
        v-if="isLocked"
        type="button"
        class="btn btn-sm btn-outline-secondary border-0 buttonn-lock"
        @click="unlockField"
      >
        <i class="fas fa-lock"></i>
      </button>
      <button
        v-else
        type="button"
        class="btn btn-outline-primary btn-sm border-0 buttonn-lock"
        @click="lockField"
      >
        <i class="fas fa-lock-open"></i>
      </button>
    </div>

    <!-- Base: siempre string en field.key (name, description, …) -->
    <input
      v-if="isPlainTextType"
      :value="baseDisplayValue"
      :id="computedFieldId"
      class="form-control"
      :class="{
        'calculated-field': isLocked,
        'readonly-field': calculated || field.readonly,
      }"
      style="background: none"
      :placeholder="placeholder"
      type="text"
      @blur="onBlurChange($event.target.value)"
      @focus="onFocus"
    />

    <input
      v-else-if="field.type === 'email'"
      :value="baseDisplayValue"
      :id="computedFieldId"
      class="form-control"
      :class="{
        'calculated-field': isLocked,
        'readonly-field': calculated || field.readonly,
      }"
      style="background: none"
      :placeholder="placeholder"
      type="email"
      @blur="onBlurChange($event.target.value)"
      @focus="onFocus"
    />

    <div
      v-else-if="useCkEditorForTextarea"
      class="ckeditor-textarea-wrap"
      :class="{ 'is-readonly': ckEditorDisabled }"
    >
      <ckeditor
        :editor="ckeditorEditor"
        :value="ckEditorValue"
        :config="ckeditorConfig"
        :disabled="ckEditorDisabled"
        @input="onCkEditorInput"
        @ready="onCkEditorReady"
        @focus="onFocus"
        @blur="onCkEditorBlur"
      />
    </div>

    <textarea
      v-else-if="field.type === 'textarea'"
      :value="baseDisplayValue"
      :id="computedFieldId"
      class="form-control"
      :class="{
        'readonly-field': calculated || field.readonly,
      }"
      style="background: none"
      :placeholder="placeholder"
      rows="4"
      cols="50"
      @blur="onBlurChange($event.target.value)"
      @focus="onFocus"
    ></textarea>

    <!-- Traducciones: siblings name_es, name_en, … (no sustituyen el base) -->
    <div v-if="isTranslatable" class="field-text-i18n mt-2">
      <div
        v-for="code in translatableLocales"
        :key="'i18n-' + field.key + '-' + code"
        class="field-text-i18n-row mb-2"
      >
        <label
          class="field-text-i18n-label small text-muted mb-0"
          :for="computedFieldId + '-' + code"
        >
          {{ languageNameForCode(code) }}
        </label>
        <textarea
          v-if="field.type === 'textarea'"
          :id="computedFieldId + '-' + code"
          class="form-control"
          :class="{ 'readonly-field': calculated || field.readonly }"
          style="background: none"
          :placeholder="placeholder"
          rows="3"
          :value="localeDraft[code] || ''"
          @blur="onLocaleSiblingBlur(code, $event.target.value)"
          @focus="onFocus"
        ></textarea>
        <input
          v-else
          :id="computedFieldId + '-' + code"
          class="form-control"
          :class="{
            'calculated-field': isLocked,
            'readonly-field': calculated || field.readonly,
          }"
          style="background: none"
          :placeholder="placeholder"
          type="text"
          :value="localeDraft[code] || ''"
          @blur="onLocaleSiblingBlur(code, $event.target.value)"
          @focus="onFocus"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import CKEditor from "@ckeditor/ckeditor5-vue2";
import "@ckeditor/ckeditor5-build-classic/build/translations/es.js";
import {
  coerceBaseFieldString,
  getFieldTranslatableLocales,
  getLocaleSiblingPath,
  isFieldTranslatable,
} from "@/utils/fieldValueI18n";

export default {
  name: "EditFieldText",
  components: {
    ckeditor: CKEditor.component,
  },
  inject: {
    formContext: { default: null },
  },
  props: {
    field: {
      type: Object,
      required: true,
    },
    value: {
      type: [String, Number, Array, Object],
      default: null,
    },
    /** Path en el form engine (p. ej. name o grupo.0.title) para siblings */
    valuePath: {
      type: String,
      default: null,
    },
    /** Valores por idioma desde FormRenderer (sincronizados al cargar el item) */
    localeValues: {
      type: Object,
      default: () => ({}),
    },
    fieldId: {
      type: String,
      default: null,
    },
    editingFields: {
      type: String,
      default: "",
    },
    disableLock: {
      type: Boolean,
      default: false,
    },
    calculated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activelyEditing: false,
      ckeditorEditor: ClassicEditor,
      ckeditorInstance: null,
      localeDraft: {},
    };
  },
  computed: {
    isTranslatable() {
      return (
        isFieldTranslatable(this.field) && this.translatableLocales.length > 0
      );
    },
    translatableLocales() {
      return getFieldTranslatableLocales(this.field);
    },
    resolvedValuePath() {
      return this.valuePath || this.field.key || "";
    },
    isPlainTextType() {
      return (
        this.field.type === "string" ||
        this.field.type === "text" ||
        this.field.type === "varchar" ||
        this.field.type === "char" ||
        this.field.type === "autoincrement"
      );
    },
    baseDisplayValue() {
      return coerceBaseFieldString(
        this.value !== null && this.value !== undefined && this.value !== ""
          ? this.value
          : this.field.defaultValue
      );
    },
    placeholder() {
      return this.field.label || this.field.key || "";
    },
    computedFieldId() {
      return this.fieldId || `field-text-${this.field.key}`;
    },
    isLocked() {
      if (this.disableLock) return false;
      if (this.activelyEditing) return false;
      if (!this.value) return false;
      if (this.field.safe && this.editingFields !== this.field.key) return true;
      return false;
    },
    useCkEditorForTextarea() {
      if (this.field.type !== "textarea") return false;
      const h = this.field.html;
      return h === true || h === "true" || h === 1 || h === "1";
    },
    ckEditorDisabled() {
      return !!(this.calculated || this.field.readonly || this.isLocked);
    },
    ckEditorValue() {
      return this.baseDisplayValue;
    },
    ckeditorConfig() {
      const loc = this.$i18n && this.$i18n.locale;
      const lang =
        typeof loc === "string" && loc.startsWith("es") ? "es" : "en";
      return {
        licenseKey: "GPL",
        language: lang,
        toolbar: {
          items: [
            "heading",
            "|",
            "bold",
            "italic",
            "link",
            "bulletedList",
            "numberedList",
            "|",
            "outdent",
            "indent",
            "|",
            "blockQuote",
            "insertTable",
            "|",
            "undo",
            "redo",
          ],
        },
      };
    },
  },
  watch: {
    localeValues: {
      immediate: true,
      deep: true,
      handler(val) {
        this.syncLocaleDraft(val);
      },
    },
    value: {
      handler() {
        // Tras cargar el item, el value base cambia y el engine ya tiene siblings
        this.$nextTick(() => this.syncLocaleDraft(this.localeValues));
      },
    },
    translatableLocales: {
      handler() {
        this.syncLocaleDraft(this.localeValues);
      },
    },
  },
  methods: {
    syncLocaleDraft(fromProp) {
      const map = {};
      this.translatableLocales.forEach((code) => {
        let v =
          fromProp && fromProp[code] != null && fromProp[code] !== ""
            ? String(fromProp[code])
            : "";
        if (!v && this.formContext && typeof this.formContext.getValue === "function") {
          const path = this.localeSiblingPath(code);
          const fromCtx = this.formContext.getValue(path);
          if (fromCtx != null && fromCtx !== "") v = String(fromCtx);
        }
        map[code] = v;
      });
      this.localeDraft = map;
    },
    languageNameForCode(code) {
      return (
        (this.$getLanguageName && this.$getLanguageName(code)) ||
        String(code).toUpperCase()
      );
    },
    localeSiblingPath(code) {
      return getLocaleSiblingPath(this.resolvedValuePath, code);
    },
    onLocaleSiblingBlur(code, newValue) {
      this.onBlur();
      const path = this.localeSiblingPath(code);
      const str = newValue != null ? String(newValue) : "";
      this.$set(this.localeDraft, code, str);
      if (this.formContext && typeof this.formContext.setValue === "function") {
        this.activelyEditing = true;
        this.formContext.setValue(path, str);
        return;
      }
      this.$emit("sibling-input", { locale: code, path, value: str });
    },
    onBlurChange(newValue) {
      this.onBlur();
      this.onFieldChange(coerceBaseFieldString(newValue));
    },
    onFieldChange(newValue) {
      this.activelyEditing = true;
      this.$emit("input", newValue);
    },
    onBlur() {
      if (this.field.safe && this.editingFields !== this.field.key) {
        this.activelyEditing = false;
      }
    },
    onFocus() {
      if (this.field.safe && this.editingFields !== this.field.key) {
        this.activelyEditing = true;
      }
    },
    lockField() {
      this.$emit("update:editingFields", "");
      this.activelyEditing = false;
    },
    unlockField() {
      this.$emit("update:editingFields", this.field.key);
      this.activelyEditing = true;
    },
    onCkEditorInput(html) {
      this.onFieldChange(html != null ? String(html) : "");
    },
    onCkEditorReady(editor) {
      this.ckeditorInstance = editor;
    },
    onCkEditorBlur() {
      this.onBlur();
    },
  },
  beforeDestroy() {
    this.ckeditorInstance = null;
  },
};
</script>

<style scoped>
.field-text-edit {
  width: 100%;
}

.form-control {
  padding: 0 7px !important;
}

.readonly-field {
  background-color: #f8f9fa !important;
  border-color: #dee2e6 !important;
  color: #6c757d !important;
  cursor: not-allowed !important;
  opacity: 0.8;
}

.calculated-field {
  background-color: #f8f9fa !important;
  border-color: #dee2e6 !important;
  cursor: not-allowed !important;
}

.readonly-field:focus {
  box-shadow: none !important;
  border-color: #dee2e6 !important;
}

.readonly-field::placeholder {
  color: #adb5bd !important;
}

.ckeditor-textarea-wrap {
  width: 100%;
}

.ckeditor-textarea-wrap ::v-deep .ck-editor__editable {
  min-height: 200px;
}

.ckeditor-textarea-wrap.is-readonly ::v-deep .ck-toolbar {
  opacity: 0.85;
}

.field-text-i18n-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.field-text-i18n-label {
  font-weight: 500;
}
</style>
